import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config/api';
import { toast } from 'react-toastify';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  CircularProgress,
  FormGroup,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon
} from '@mui/icons-material';



// Validation schema
const UserSchema = Yup.object().shape({
  email: Yup.string()
    .email('Invalid email address')
    .required('Email is required'),
  firstName: Yup.string()
    .required('First name is required')
    .min(2, 'First name must be at least 2 characters'),
  lastName: Yup.string()
    .required('Last name is required')
    .min(2, 'Last name must be at least 2 characters'),
  password: Yup.string()
    .when('$isEditing', {
      is: false,
      then: (schema) => schema.required('Password is required').min(6, 'Password must be at least 6 characters'),
      otherwise: (schema) => schema.min(6, 'Password must be at least 6 characters')
    })
    .test('password-strength', 'Password is too weak', function(value) {
      if (!value || value.length < 6) return true; // Let other validations handle this
      
      // Check for weak passwords
      const weakPasswords = [
        'password', '123456', '12345678', 'qwerty', 'abc123', 'password123',
        'admin', 'user', 'test', 'demo', 'welcome', 'letmein', 'monkey',
        'dragon', 'master', 'hello', 'freedom', 'whatever', 'qazwsx',
        'trustno1', 'jordan', 'harley', 'ranger', 'iwantu', 'jennifer',
        'joshua', 'maggie', 'buster', 'shadow', 'michael', 'mustang',
        'baseball', 'football', 'superman', 'batman', 'spider', 'hunter'
      ];
      
      const lowerValue = value.toLowerCase();
      if (weakPasswords.includes(lowerValue)) {
        return this.createError({ message: 'This password is too common. Please choose a stronger password.' });
      }
      
      // Check for sequential characters
      if (/(.)\1{2,}/.test(value)) {
        return this.createError({ message: 'Password contains too many repeated characters.' });
      }
      
      // Check for simple patterns
      if (/^(.)\1+$/.test(value) || /^1234567890*$/.test(value) || /^qwertyuiop*$/.test(value)) {
        return this.createError({ message: 'Password is too predictable. Please choose a stronger password.' });
      }
      
      return true;
    }),
  department: Yup.string().required('Department is required'),
  jobTitle: Yup.string(),
  officeLocation: Yup.string(),
  roles: Yup.array()
    .min(1, 'At least one role must be selected'),
  status: Yup.string().required('Status is required')
});

const AdminUserManagement = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const { getAuthHeaders } = useAuth();
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openModal, setOpenModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showITPrompt, setShowITPrompt] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');

  const filteredUsers = users.filter((user) => {
    const term = searchTerm.toLowerCase();
  
    return (
      user.firstName?.toLowerCase().includes(term) ||
      user.lastName?.toLowerCase().includes(term) ||
      user.email?.toLowerCase().includes(term)
    );
  });
  // Available roles
  const availableRoles = ['admin', 'itsupport', 'employee'];

  // Initial form values for new user
  const initialValues = {
    email: '',
    firstName: '',
    lastName: '',
    password: '',
    department: '',
    jobTitle: '',
    officeLocation: '',
    roles: ['employee'],
    status: 'active'
  };

  // Fetch all users
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/users`, {
        headers: getAuthHeaders(),
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }
      
      const data = await response.json();
      setUsers(data);
    } catch (error) {
      setError('Error fetching users: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch all departments
  const fetchDepartments = async () => {
    try {
      const response = await fetch(`${API_URL}/departments`, {
        headers: getAuthHeaders(),
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch departments');
      }
      
      const data = await response.json();
      setDepartments(data);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchDepartments();
  }, []);

  // Reset form
  const resetForm = () => {
    setEditingUser(null);
  };

  // Open modal for adding new user
  const handleAddUser = () => {
    setEditingUser(null); // Clear editing user to ensure clean form
    setOpenModal(true);
  };

  // Open modal for editing user
  const handleEditUser = (user) => {
    setEditingUser(user);
    setOpenModal(true);
  };

  // Handle form submission (create or update)
  const handleSubmit = async (values, { setSubmitting, resetForm: resetFormik }) => {
    await performSubmit(values, { setSubmitting, resetForm: resetFormik });
  };

  // Actual submission logic
  const performSubmit = async (values, { setSubmitting, resetForm: resetFormik }) => {
    try {
      const url = editingUser 
        ? `${API_URL}/users/${editingUser._id}`
        : `${API_URL}/users`;
      
      const method = editingUser ? 'PUT' : 'POST';
      
      // For updates, only include password if it's not empty
      let body;
      if (editingUser) {
        const { password, ...otherFields } = values;
        body = { ...otherFields };
        // Only include password if it's not empty
        if (password && password.trim() !== '') {
          body.password = password;
        }
      } else {
        body = values;
      }

      const response = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Operation failed');
      }
      const resultData = await response.json();
      
      if (!editingUser) {
        setNewUserEmail(resultData.email);
        setShowITPrompt(true);
        toast.success('User created successfully!');
      } else {
        toast.success('User updated successfully!');
      }

      setOpenModal(false);
      resetForm();
      resetFormik();
      fetchUsers(); // Refresh the list
    } catch (error) {
      setError('Error: ' + error.message);
      toast.error('Operation failed: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle user deletion
  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/users/${userId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to delete user');
      }

      toast.success('User deleted successfully!');
      fetchUsers(); // Refresh the list
    } catch (error) {
      setError('Error deleting user: ' + error.message);
      toast.error('Failed to delete user: ' + error.message);
    }
  };

  // Get role display name
  const getRoleDisplayName = (role) => {
    switch (role) {
      case 'admin': return 'Admin';
      case 'itsupport': return 'IT Support';
      case 'employee': return 'Employee';
      default: return role;
    }
  };

  // Get role color
  const getRoleColor = (role) => {
    switch (role) {
      case 'admin': return 'error';
      case 'itsupport': return 'warning';
      case 'employee': return 'primary';
      default: return 'default';
    }
  };

  const handleSendOnboardingEmail = async (user) => {
    try {
      const res = await fetch(`${API_URL}/users/${user._id}/send-onboarding-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await res.json();

      if (res.ok) {
        toast.success('Onboarding Form sent successfully!');
      } else {
        toast.error(data.message || 'Failed to sent Email');
      }
    } catch (err) {
      toast.error('Error sending onboarding email');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: 'background.default', p: 3, width: '100%', overflowX: 'hidden' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600, fontSize: '30px' ,lineHeight:1.5 ,letterSpacing:1}}>
          User Management
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <TextField
            size="small"
            label="Search Users"
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ width: 280 }}
          />
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddUser}
          sx={{ textTransform: 'none' }}
        >
          Add New User
        </Button>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 3 }}
          action={
            <IconButton
              color="inherit"
              size="small"
              onClick={() => setError('')}
            >
              <CloseIcon />
            </IconButton>
          }
        >
          {error}
        </Alert>
      )}

      

      {/* Users Table */}
      <TableContainer 
        component={Paper} 
        sx={{ 
          boxShadow: 2,
          maxWidth: '100%',
          overflowX: 'auto',
          width: '100%'
        }}
      >
        <Table sx={{ minWidth: 1200, tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'primary.main' }}>
              <TableCell sx={{ color: 'white', fontWeight: 600, width: '12%' }}>Name</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600, width: '15%' }}>Email</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600, width: '10%' }}>Department</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600, width: '10%' }}>Job Title</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600, width: '12%' }}>Roles</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600, width: '10%' }}>Primary Role</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600, width: '12%' }}>Status</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600, width: '10%' }}>IT Onboarding</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600, width: '9%' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
          {filteredUsers.map(user => (
              <TableRow key={user._id} sx={{ '&:hover': { backgroundColor: 'action.hover' } }}>
                <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {user.firstName} {user.lastName}
                </TableCell>
                <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {user.email}
                </TableCell>
                <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {user.department || 'General'}
                </TableCell>
                <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {user.jobTitle || '-'}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {(user.roles || [user.role]).map(role => (
                      <Chip
                        key={role}
                        label={getRoleDisplayName(role)}
                        color={getRoleColor(role)}
                        size="small"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    label={getRoleDisplayName(user.primaryRole || user.role)}
                    color={getRoleColor(user.primaryRole || user.role)}
                    size="small"
                    variant="filled"
                  />
                </TableCell>
                <TableCell>  
                  <Box>
                    <Chip
                      label={user.status || 'active'}
                      color={user.status === 'active' ? 'success' : 'default'}
                      size="small"
                      variant="outlined"
                    />
                    <Typography
                      component="a"
                      onClick={() => handleSendOnboardingEmail(user)}
                      sx={{
                        display: 'block',
                        fontSize: '0.75rem',
                        color: 'primary.main',
                        textDecoration: 'underline',
                        mt: 0.5,
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        p: 0,
                      }}
                    >
                      send Onboarding Email
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  {user.itOnboardingStatus === 'completed' ? (
                    <Chip label="Completed" color="success" size="small" />
                  ) : (
                    <a
                      variant="outlined"
                      size="small"
                      href={`/itonboarding-form?email=${encodeURIComponent(user.email)}`}
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      Complete Form
                    </a>
                  )}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleEditUser(user)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteUser(user._id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Modal for Add/Edit User */}
      <Dialog 
        open={openModal} 
        onClose={() => {
          setOpenModal(false);
          resetForm();
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            {editingUser ? 'Edit User' : 'Add New User'}
          </Typography>
          <IconButton
            onClick={() => {
              setOpenModal(false);
              resetForm();
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <Formik
          key={editingUser ? editingUser._id : 'new-user'} // Force re-render when switching between add/edit
          enableReinitialize={true}
          initialValues={editingUser ? {
            email: editingUser.email,
            firstName: editingUser.firstName,
            lastName: editingUser.lastName,
            password: '', // Don't populate password for editing
            department: editingUser.department || 'General',
            jobTitle: editingUser.jobTitle || '',
            officeLocation: editingUser.officeLocation || '',
            roles: editingUser.roles || ['employee'],
            status: editingUser.status || 'active'
          } : initialValues}
          validationSchema={UserSchema}
          context={{ isEditing: !!editingUser }}
          validateOnMount={false}
          validateOnChange={true}
          validateOnBlur={true}
          onSubmit={handleSubmit}
        >
                     {({ values, errors, touched, handleChange, handleBlur, setFieldValue, isSubmitting, validateForm }) => (
            <Form>
              <DialogContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Field
                      as={TextField}
                      fullWidth
                      label="First Name"
                      name="firstName"
                      value={values.firstName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.firstName && Boolean(errors.firstName)}
                      helperText={touched.firstName && errors.firstName}
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Field
                      as={TextField}
                      fullWidth
                      label="Last Name"
                      name="lastName"
                      value={values.lastName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.lastName && Boolean(errors.lastName)}
                      helperText={touched.lastName && errors.lastName}
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Field
                      as={TextField}
                      fullWidth
                      label="Email"
                      name="email"
                      type="email"
                      value={values.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.email && Boolean(errors.email)}
                      helperText={touched.email && errors.email}
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Field
                      as={TextField}
                      fullWidth
                      label={`Password ${!editingUser ? '*' : ''}`}
                      name="password"
                      type="password"
                      value={values.password}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.password && Boolean(errors.password)}
                      helperText={touched.password && errors.password}
                      placeholder={editingUser ? "Leave blank to keep current password" : "Enter password"}
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth margin="normal" error={touched.department && Boolean(errors.department)}>
                      <InputLabel>Department</InputLabel>
                      <Select
                        name="department"
                        value={values.department}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        label="Department"
                      >
                        <MenuItem value="">
                          <em>Select a department</em>
                        </MenuItem>
                        {departments.length > 0 ? (
                          departments.map((department) => (
                            <MenuItem key={department._id} value={department.departmentName}>
                              {department.departmentName}
                            </MenuItem>
                          ))
                        ) : (
                          <MenuItem disabled>
                            {loading ? 'Loading departments...' : 'No departments available'}
                          </MenuItem>
                        )}
                      </Select>
                      {touched.department && errors.department && (
                        <Typography color="error" variant="caption" sx={{ mt: 0.5, ml: 1.75 }}>
                          {errors.department}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Field
                      as={TextField}
                      fullWidth
                      label="Job Title"
                      name="jobTitle"
                      value={values.jobTitle}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Field
                      as={TextField}
                      fullWidth
                      label="Office Location"
                      name="officeLocation"
                      value={values.officeLocation}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      margin="normal"
                    />
                  </Grid>
                  
                  {/* Roles Selection */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, fontSize: '20px' }}>
                      Assign Roles
                    </Typography>
                                         <FormGroup row>
                       {availableRoles.map(role => (
                         <FormControlLabel
                           key={role}
                           control={
                             <Checkbox
                               checked={values.roles.includes(role)}
                               disabled={role === 'employee'} // Employee is always selected
                               onChange={(e) => {
                                  let newRoles;
                                  if (e.target.checked) {
                                    // Adding a role
                                    newRoles = [...values.roles, role];
                                    
                                    // If adding admin, remove itsupport
                                    if (role === 'admin') {
                                      newRoles = newRoles.filter(r => r !== 'itsupport');
                                    }
                                    
                                    // If adding itsupport, remove admin
                                    if (role === 'itsupport') {
                                      newRoles = newRoles.filter(r => r !== 'admin');
                                    }
                                  } else {
                                    // Removing a role
                                    newRoles = values.roles.filter(r => r !== role);
                                    
                                    // Ensure employee is always selected
                                    if (!newRoles.includes('employee')) {
                                      newRoles.push('employee');
                                    }
                                  }
                                  setFieldValue('roles', newRoles, true);
                                }}
                               name={role}
                             />
                           }
                           label={getRoleDisplayName(role)}
                           sx={{ mr: 2 }}
                         />
                       ))}
                     </FormGroup>
                                         {touched.roles && errors.roles && (
                       <Typography color="error" variant="caption" sx={{ display: 'block', mt: 1 }}>
                         {errors.roles}
                       </Typography>
                     )}
                     
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth margin="normal" error={touched.status && Boolean(errors.status)}>
                      <InputLabel>Status</InputLabel>
                      <Select
                        name="status"
                        value={values.status}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        label="Status"
                      >
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="left">Left</MenuItem>
                      </Select>
                      {touched.status && errors.status && (
                        <Typography color="error" variant="caption">
                          {errors.status}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>
                                 </Grid>
                 

               </DialogContent>
               
               <DialogActions sx={{ p: 3 }}>
                 <Button
                   onClick={() => {
                     setOpenModal(false);
                     resetForm();
                   }}
                   sx={{ textTransform: 'none' }}
                   disabled={isSubmitting}
                 >
                   Cancel
                 </Button>
                 <Button
                   type="submit"
                   variant="contained"
                   sx={{ textTransform: 'none' }}
                   disabled={isSubmitting}
                 >
                   {isSubmitting ? 'Saving...' : (editingUser ? 'Update User' : 'Create User')}
                 </Button>
               </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>

             <Dialog
         open={showITPrompt}
         onClose={() => setShowITPrompt(false)}
       >
         <DialogTitle>Please fill the IT Onboarding Form</DialogTitle>
         <DialogContent>
           <Typography>
             IT onboarding form needs to be completed for the newly added user.
           </Typography>
         </DialogContent>
         <DialogActions>
           <Button onClick={() => setShowITPrompt(false)}>Close</Button>
           <Button
             variant="contained"
             color="primary"
             onClick={() => {
               setShowITPrompt(false);
               window.open(
                 `/itonboarding-form?email=${encodeURIComponent(newUserEmail)}`,
                 '_blank',
                 'noopener,noreferrer'
               );
             }}
           >
             Go to Form
           </Button>
         </DialogActions>
       </Dialog>
    </Box>
  );
};

export default AdminUserManagement; 